/**
 * UserIssuesTable — editorial restyle of the user directory.
 *
 * Phase-5b: matches the Section 04 ("People needing a hand") editorial
 * pattern from UserInsights.tsx — same tone-accent palette, italic-mixed
 * headline, dropcap pagination numerals, named-subject voice.
 *
 * The component's external contract is unchanged: same props, same callbacks,
 * same sort/filter/pagination/export behavior. Only the visual surface and
 * copy lean editorial.
 */

import { useMemo, useState, useCallback } from 'react'
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  UsersIcon,
  ChartBarIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline'
import type { UserDetailItem, UserIssueFilter } from '@/types'
import { DEFAULT_INACTIVITY_THRESHOLD_DAYS } from '@/types'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

interface Props {
  users: UserDetailItem[]
  total: number
  currentPage: number
  totalPages: number
  selectedFilter: UserIssueFilter
  onFilterChange: (filter: UserIssueFilter) => void
  onPageChange: (page: number) => void
  isLoading: boolean
  inactivityThresholdDays?: number
  tenantId?: string
  filterCounts?: Record<string, number>
  resourceAccountCount?: number
  /**
   * When true, the table view AND the CSV export narrow to Global Admins
   * only. Mirrors the `?scope=globalAdmins` URL contract honored by the
   * parent UserInsights page. Without this flag the export silently returns
   * the full unscoped population, breaking the "this download matches the
   * page I'm looking at" promise.
   */
  globalAdminsOnly?: boolean
}

type TabKey = 'all' | 'activity' | 'attention' | 'security'

interface TabDefinition {
  key: TabKey
  label: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  badgeKey?: string
  badgeStyle?: 'attention' | 'default'
  filters: { key: UserIssueFilter; label: string; description: string }[]
}

function createTabs(threshold: number): TabDefinition[] {
  return [
    {
      key: 'all',
      label: 'All Users',
      icon: UsersIcon,
      badgeKey: 'All',
      filters: [
        { key: 'All', label: 'All', description: 'All users in the tenant' },
        { key: 'ActiveMembers', label: 'Active Members', description: `Enabled member accounts with sign-in activity within ${threshold} days` },
        { key: 'ActiveGuests', label: 'Active Guests', description: `Enabled guest accounts with sign-in activity within ${threshold} days` },
        { key: 'ResourceAccounts', label: 'Resource Accounts', description: 'Room mailboxes, equipment, Bookings pages, Teams Phone/Rooms accounts' },
      ],
    },
    {
      key: 'activity',
      label: 'Activity',
      icon: ChartBarIcon,
      filters: [
        { key: 'EnabledInactiveMembers', label: 'Enabled Inactive Members', description: `Enabled member accounts inactive for ${threshold}+ days` },
        { key: 'InactiveMembers', label: 'Inactive Members', description: `Member accounts inactive for ${threshold}+ days` },
        { key: 'InactiveGuests', label: 'Inactive Guests', description: `Guest accounts inactive for ${threshold}+ days` },
      ],
    },
    {
      key: 'attention',
      label: 'Attention',
      icon: ExclamationTriangleIcon,
      badgeKey: 'NeedsAttention',
      badgeStyle: 'attention',
      filters: [
        { key: 'NeedsAttention', label: 'All Issues', description: 'Users with any issue (license waste, no MFA, or inactive)' },
        { key: 'DisabledWithLicenses', label: 'Disabled w/ Licenses', description: 'Disabled accounts still consuming licenses' },
        { key: 'InactiveWithLicenses', label: 'Inactive w/ Licenses', description: `Users inactive for ${threshold}+ days with licenses` },
        { key: 'LicenseWaste', label: 'License Waste', description: 'All license optimization opportunities' },
      ],
    },
    {
      key: 'security',
      label: 'Security',
      icon: ShieldExclamationIcon,
      filters: [
        { key: 'NoMfa', label: 'No MFA', description: 'Users without MFA methods registered' },
        { key: 'NoAuthenticatorApp', label: 'No Authenticator', description: 'Users without Authenticator app' },
        { key: 'NoPhishResistantMfa', label: 'No Phish-Resistant MFA', description: 'Users with MFA but no phish-resistant method (FIDO2, Windows Hello, Certificate)' },
        { key: 'HasPhishResistantMfa', label: 'Has Phish-Resistant MFA', description: 'Users with phish-resistant MFA enrolled' },
      ],
    },
  ]
}

// Map each filter to the tab that contains it
function getTabForFilter(tabs: TabDefinition[], filter: UserIssueFilter): TabKey {
  for (const tab of tabs) {
    if (tab.filters.some(f => f.key === filter)) return tab.key
  }
  return 'all'
}

// Check if the current filter is an inactive-related filter
const isInactiveFilter = (filter: UserIssueFilter): boolean => {
  return filter === 'InactiveWithLicenses' || filter === 'InactiveMembers' || filter === 'InactiveGuests'
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export default function UserIssuesTable({
  users,
  total,
  currentPage,
  totalPages,
  selectedFilter,
  onFilterChange,
  onPageChange,
  isLoading,
  inactivityThresholdDays,
  tenantId,
  filterCounts,
  resourceAccountCount,
  globalAdminsOnly = false,
}: Props) {
  const threshold = inactivityThresholdDays ?? DEFAULT_INACTIVITY_THRESHOLD_DAYS
  const tabs = useMemo(() => createTabs(threshold), [threshold])
  const [isExporting, setIsExporting] = useState(false)

  // Active tab tracks which category tab is selected — auto-syncs when filter changes
  const activeTab = useMemo(() => getTabForFilter(tabs, selectedFilter), [tabs, selectedFilter])
  const activeTabDef = tabs.find(t => t.key === activeTab)!

  const needsAttentionCount = filterCounts?.['NeedsAttention'] ?? 0

  // Find the label + description for current filter (for headline + empty state)
  const currentFilterMeta = useMemo(() => {
    for (const tab of tabs) {
      const found = tab.filters.find(f => f.key === selectedFilter)
      if (found) return found
    }
    return null
  }, [tabs, selectedFilter])

  const currentFilterLabel = currentFilterMeta?.label ?? String(selectedFilter)
  const currentFilterDescription = currentFilterMeta?.description ?? ''

  const handleTabClick = (tabKey: TabKey) => {
    const tab = tabs.find(t => t.key === tabKey)
    if (tab) {
      onFilterChange(tab.filters[0].key)
    }
  }

  const exportToCsv = useCallback(async () => {
    if (!tenantId) return
    setIsExporting(true)
    try {
      const pageSize = 100
      // Pass `globalAdminsOnly` through so the SERVER narrows each export
      // page to GAs when the page is GA-scoped. Without this the export
      // would re-fetch the full population and rely on a post-filter that
      // can't see rows beyond what was admitted to the response.
      const firstPage = await api.getUserInsightsUsers(
        tenantId, selectedFilter, 1, pageSize, globalAdminsOnly,
      )
      const allUsers: UserDetailItem[] = [...firstPage.items]
      const totalPages = firstPage.totalPages

      for (let page = 2; page <= totalPages; page++) {
        const result = await api.getUserInsightsUsers(
          tenantId, selectedFilter, page, pageSize, globalAdminsOnly,
        )
        allUsers.push(...result.items)
      }

      // Defense-in-depth: the server-side `globalAdminsOnly` filter is the
      // primary contract, but we keep the post-fetch GA filter as a belt-
      // and-braces guard. Costs nothing on small payloads, protects the
      // export's "this matches the view" promise if a future server change
      // ever weakens the filter.
      const exportRows = globalAdminsOnly
        ? allUsers.filter(u => u.isGlobalAdmin === true)
        : allUsers

      const headers = [
        'Display Name', 'Email', 'Job Title', 'Account Status', 'User Type',
        'Shared Mailbox', 'Resource Account', 'Resource Account Type',
        'Last Sign-In', 'Account Created Date', 'Inactive', 'License Count',
        'Assigned Licenses', 'MFA Enabled', 'Authenticator App',
        'Phish-Resistant MFA', 'FIDO2 Key', 'Windows Hello', 'Certificate Auth',
        'Issue Type',
      ]
      const rows = exportRows.map((user: UserDetailItem) => [
        escapeCsvField(user.displayName || ''),
        escapeCsvField(user.userPrincipalName || ''),
        escapeCsvField(user.jobTitle || ''),
        user.accountEnabled ? 'Enabled' : 'Disabled',
        user.userType || '',
        user.isSharedMailbox ? 'Yes' : 'No',
        user.isResourceAccount ? 'Yes' : 'No',
        user.resourceAccountType || '',
        user.lastSignInDateTime || 'Never',
        user.accountCreatedDate || '',
        user.isInactive ? 'Yes' : 'No',
        String(user.licenseCount ?? 0),
        escapeCsvField(user.assignedLicenses?.join('; ') || ''),
        user.hasMfaEnabled ? 'Yes' : 'No',
        user.hasAuthenticatorApp ? 'Yes' : 'No',
        user.hasPhishResistantMfa ? 'Yes' : 'No',
        user.hasFido2Key ? 'Yes' : 'No',
        user.hasWindowsHello ? 'Yes' : 'No',
        user.hasCertificateAuth ? 'Yes' : 'No',
        user.issueType || '',
      ])
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().split('T')[0]
      const filterLabel = selectedFilter.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '')
      const scopeLabel = globalAdminsOnly ? '-global-admins' : ''
      link.download = `user-directory-${filterLabel}${scopeLabel}-${timestamp}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success(`Exported ${exportRows.length} users to CSV`)
    } catch (error) {
      console.error('CSV export failed:', error)
      toast.error('Failed to export CSV')
    } finally {
      setIsExporting(false)
    }
  }, [tenantId, selectedFilter, globalAdminsOnly])

  return (
    <section
      data-tone="identity"
      className="rounded-3xl border border-dark-600 bg-dark-800 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]"
    >
      {/* ------------------------------------------------------------------ */}
      {/* Editorial header                                                    */}
      {/* ------------------------------------------------------------------ */}
      <header className="px-7 pt-7 pb-5 border-b border-dark-700">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
              User directory · {activeTabDef.label.toLowerCase()}
            </div>
            <h3 className="mt-1 text-[22px] font-extrabold tracking-[-0.015em] text-slate-100 dark:text-slate-100">
              Directory,{' '}
              <em
                style={{
                  fontStyle: 'italic',
                  fontWeight: 600,
                  color: 'var(--tone-accent-deep)',
                }}
              >
                {currentFilterLabel.toLowerCase()}
              </em>
              .
            </h3>
            <p className="mt-1.5 text-[12px] leading-[1.55] text-slate-400">
              <span className="font-display tabular-nums font-semibold text-slate-200">
                {total.toLocaleString()}
              </span>{' '}
              {total === 1 ? 'person' : 'people'}
              {currentFilterDescription ? ` · ${currentFilterDescription}` : ''}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {needsAttentionCount > 0 && activeTab !== 'attention' && (
              <button
                onClick={() => onFilterChange('NeedsAttention')}
                className="inline-flex items-center gap-2 rounded-full border border-dark-600 bg-dark-900 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-100 transition-colors hover:border-current"
                style={{ color: 'rgb(var(--color-secondary-sand-300))' }}
              >
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full animate-pulse"
                  style={{ background: 'rgb(var(--color-secondary-sand-300))' }}
                />
                <span className="font-display tabular-nums font-extrabold italic text-[14px]">
                  {needsAttentionCount.toLocaleString()}
                </span>
                <span>need attention</span>
              </button>
            )}

            {tenantId && total > 0 && (
              <button
                onClick={exportToCsv}
                disabled={isExporting}
                className="inline-flex items-center gap-2 rounded-full border border-dark-600 bg-dark-900 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-100 transition-colors hover:border-current disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: 'var(--tone-accent-deep)' }}
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                {isExporting ? 'Exporting…' : 'Export CSV →'}
              </button>
            )}
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Editorial tab strip — pill toggle, navy active                   */}
        {/* ---------------------------------------------------------------- */}
        <div className="mt-5 inline-flex flex-wrap items-center gap-1 rounded-full border border-dark-600 bg-dark-900 p-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key
            const badgeCount = tab.badgeKey ? filterCounts?.[tab.badgeKey] : undefined
            const TabIcon = tab.icon
            const isAttention = tab.badgeStyle === 'attention'
            return (
              <button
                key={tab.key}
                onClick={() => handleTabClick(tab.key)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-200 ${
                  isActive
                    ? 'bg-[#212938] text-white shadow-[0_4px_12px_rgba(33,41,56,0.18)]'
                    : 'text-slate-500 hover:text-slate-100'
                }`}
              >
                <TabIcon className={`h-3.5 w-3.5 ${isActive ? 'opacity-100' : 'opacity-50'}`} />
                {tab.label}
                {badgeCount !== undefined && badgeCount > 0 && (
                  <span
                    className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums ${
                      isActive
                        ? 'text-[#212938]'
                        : 'bg-dark-700 text-slate-300'
                    }`}
                    style={
                      isActive
                        ? {
                            background: isAttention
                              ? 'rgb(var(--color-secondary-sand-300))'
                              : 'var(--tone-accent)',
                          }
                        : undefined
                    }
                  >
                    {badgeCount.toLocaleString()}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Sub-filter chips — editorial pills                                */}
        {/* ---------------------------------------------------------------- */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {activeTabDef.filters.map((filter) => {
            const count = filterCounts?.[filter.key]
            const isSelected = selectedFilter === filter.key
            return (
              <button
                key={filter.key}
                onClick={() => onFilterChange(filter.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] transition-all ${
                  isSelected
                    ? 'border-transparent text-[#212938]'
                    : 'border-dark-600 bg-dark-900 text-slate-400 hover:text-slate-100 hover:border-dark-500'
                }`}
                style={
                  isSelected
                    ? { background: 'var(--tone-accent)' }
                    : undefined
                }
                title={filter.description}
              >
                {filter.label}
                {count !== undefined && count > 0 && (
                  <span
                    className={`font-mono text-[10px] tabular-nums ${
                      isSelected ? 'text-[#212938]/70' : 'text-slate-500'
                    }`}
                  >
                    {count.toLocaleString()}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Editorial notice blocks                                           */}
        {/* ---------------------------------------------------------------- */}
        {isInactiveFilter(selectedFilter) && (
          <NoticeBlock>
            <strong className="text-slate-100 dark:text-slate-100">Inactive</strong>{' '}
            means no interactive or non-interactive sign-in in the last {threshold} days.{' '}
            <span style={{ color: 'var(--tone-accent-deep)' }}>Shared mailboxes</span>{' '}
            are expected to show as inactive since they don't have direct sign-ins.
          </NoticeBlock>
        )}

        {(selectedFilter === 'ActiveMembers' || selectedFilter === 'ActiveGuests') && (
          <NoticeBlock>
            <strong className="text-slate-100 dark:text-slate-100">Active</strong> means
            the account is enabled and has sign-in activity within the last {threshold} days.
          </NoticeBlock>
        )}

        {(resourceAccountCount ?? 0) > 0 && (
          <NoticeBlock>
            <span className="font-display tabular-nums font-extrabold italic text-slate-100 dark:text-slate-100">
              {resourceAccountCount}
            </span>{' '}
            resource accounts (room mailboxes, equipment, Bookings, Teams Phone/Rooms) are
            excluded from <strong className="text-slate-100 dark:text-slate-100">MFA Adoption</strong>{' '}
            and <strong className="text-slate-100 dark:text-slate-100">License Waste</strong>{' '}
            metrics. These accounts use service-level or free licenses and don't support
            interactive MFA.{' '}
            <button
              onClick={() => onFilterChange('ResourceAccounts')}
              className="font-bold underline underline-offset-2"
              style={{ color: 'var(--tone-accent-deep)' }}
            >
              View resource accounts →
            </button>
          </NoticeBlock>
        )}
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Body — table or empty/loading state                                */}
      {/* ------------------------------------------------------------------ */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="p-8">
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-dark-700 rounded-lg" />
              ))}
            </div>
          </div>
        ) : users.length === 0 ? (
          <div className="px-7 py-16 text-center">
            <UsersIcon className="mx-auto h-12 w-12 text-slate-600 mb-4" />
            <p className="font-display text-[18px] font-extrabold tracking-[-0.015em] text-slate-200">
              No one matches{' '}
              <em
                style={{
                  fontStyle: 'italic',
                  fontWeight: 600,
                  color: 'var(--tone-accent-deep)',
                }}
              >
                {currentFilterLabel.toLowerCase()}
              </em>
              .
            </p>
            <p className="mt-2 text-[13px] text-slate-400">
              {selectedFilter === 'All'
                ? 'No user data available. Run a scan to collect user information.'
                : 'Try a different filter, or run a scan to refresh the data.'}
            </p>
          </div>
        ) : (
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-dark-700">
                {[
                  'User',
                  'Status',
                  'Last Sign-In',
                  'Created',
                  'Licenses',
                  'MFA',
                  'Phish-Resistant',
                  'Issue',
                ].map((label) => (
                  <th
                    key={label}
                    className="px-6 py-3.5 text-left text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.userId}
                  className="border-b border-dark-700 last:border-b-0 transition-colors hover:bg-dark-700/40"
                >
                  {/* User */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <span
                        aria-hidden
                        className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full text-[12px] font-bold tracking-[-0.01em] text-[#212938]"
                        style={{
                          background:
                            'linear-gradient(135deg, rgb(var(--color-secondary-sand-300)), rgb(var(--color-secondary-blue-300)))',
                        }}
                      >
                        {initialsOf(user.displayName)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-[13.5px] font-bold text-slate-100 dark:text-slate-100">
                          {user.displayName}
                        </p>
                        <p className="mt-0.5 text-[11.5px] text-slate-400">
                          {user.userPrincipalName}
                        </p>
                        {user.jobTitle && (
                          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            {user.jobTitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <EditorialChip
                        label={user.accountEnabled ? 'Enabled' : 'Disabled'}
                        kind={user.accountEnabled ? 'low' : 'crit'}
                      />
                      <EditorialChip
                        label={user.userType}
                        kind={user.userType === 'Member' ? 'tone' : 'med'}
                      />
                      {user.isSharedMailbox && (
                        <EditorialChip label="Shared" kind="muted" title="Shared Mailbox — sign-in activity not expected" />
                      )}
                      {user.isResourceAccount && (
                        <EditorialChip
                          label={
                            user.resourceAccountType === 'RoomMailbox' ? 'Room' :
                            user.resourceAccountType === 'EquipmentMailbox' ? 'Equipment' :
                            user.resourceAccountType === 'SchedulingMailbox' ? 'Bookings' :
                            user.resourceAccountType === 'TeamsPhone' ? 'Teams Phone' :
                            user.resourceAccountType === 'TeamsRooms' ? 'Teams Room' :
                            'Resource'
                          }
                          kind="muted"
                          title={`Resource Account: ${user.resourceAccountType ?? 'Unknown'} — excluded from MFA compliance`}
                        />
                      )}
                    </div>
                  </td>

                  {/* Last sign-in */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.lastSignInDateTime ? (
                      <div>
                        <p
                          className={`text-[13px] ${
                            user.isInactive ? '' : 'text-slate-200'
                          }`}
                          style={user.isInactive ? { color: '#E57373' } : undefined}
                        >
                          {formatDate(user.lastSignInDateTime)}
                        </p>
                        {user.isInactive && (
                          <p
                            className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: '#E57373' }}
                          >
                            Inactive {threshold}+ days
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-[13px] italic text-slate-500">Never</span>
                    )}
                  </td>

                  {/* Created */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.accountCreatedDate ? (
                      <div>
                        <p
                          className="text-[13px] text-slate-200"
                          title={new Date(user.accountCreatedDate).toLocaleDateString()}
                        >
                          {formatDate(user.accountCreatedDate)}
                        </p>
                        {isNewAccount(user) && (
                          <span
                            className="mt-1 inline-flex rounded-full border border-dark-600 bg-dark-900 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: 'var(--tone-accent-deep)' }}
                          >
                            New
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[13px] text-slate-500">—</span>
                    )}
                  </td>

                  {/* Licenses */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.licenseCount > 0 ? (
                      <div>
                        <p className="text-[13px] text-slate-200">
                          <span className="font-display tabular-nums font-semibold">
                            {user.licenseCount}
                          </span>{' '}
                          license{user.licenseCount === 1 ? '' : 's'}
                        </p>
                        <p
                          className="mt-0.5 max-w-[150px] truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500"
                          title={user.assignedLicenses.join(', ')}
                        >
                          {user.assignedLicenses.slice(0, 2).join(', ')}
                          {user.assignedLicenses.length > 2 && ` +${user.assignedLicenses.length - 2}`}
                        </p>
                      </div>
                    ) : (
                      <span className="text-[13px] italic text-slate-500">None</span>
                    )}
                  </td>

                  {/* MFA */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {user.hasMfaEnabled ? (
                        <CheckCircleIcon
                          className="h-5 w-5"
                          style={{ color: '#4A9B6E' }}
                          title="MFA Methods Registered"
                        />
                      ) : (
                        <XCircleIcon
                          className="h-5 w-5"
                          style={{ color: '#E57373' }}
                          title="No MFA Methods"
                        />
                      )}
                      {user.hasAuthenticatorApp ? (
                        <EditorialChip label="Auth" kind="low" />
                      ) : (
                        <span className="text-[10px] text-slate-600">—</span>
                      )}
                    </div>
                  </td>

                  {/* Phish-Resistant */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {user.hasPhishResistantMfa ? (
                        <ShieldCheckIcon
                          className="h-5 w-5"
                          style={{ color: '#4A9B6E' }}
                          title="Phish-Resistant MFA Enrolled"
                        />
                      ) : user.hasMfaEnabled ? (
                        <ExclamationTriangleIcon
                          className="h-5 w-5"
                          style={{ color: 'rgb(var(--color-secondary-sand-500))' }}
                          title="Standard MFA Only — Not Phish-Resistant"
                        />
                      ) : (
                        <XCircleIcon
                          className="h-5 w-5"
                          style={{ color: '#E57373' }}
                          title="No MFA Methods"
                        />
                      )}
                      {user.hasPhishResistantMfa && (
                        <div className="flex flex-wrap items-center gap-1">
                          {user.hasFido2Key && (
                            <EditorialChip label="FIDO2" kind="low" title="FIDO2 Security Key" />
                          )}
                          {user.hasWindowsHello && (
                            <EditorialChip label="WHfB" kind="low" title="Windows Hello for Business" />
                          )}
                          {user.hasCertificateAuth && (
                            <EditorialChip label="Cert" kind="low" title="Certificate-Based Authentication" />
                          )}
                        </div>
                      )}
                      {!user.hasPhishResistantMfa && user.hasMfaEnabled && (
                        <span
                          className="text-[10px] font-semibold uppercase tracking-[0.14em]"
                          style={{ color: 'rgb(var(--color-secondary-sand-500))' }}
                        >
                          Standard
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Issue */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EditorialChip
                      label={getIssueLabel(user.issueType)}
                      kind={getIssueKind(user.issueType)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Editorial pagination                                                */}
      {/* ------------------------------------------------------------------ */}
      {totalPages > 1 && (
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-dark-700 px-7 py-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Page{' '}
            <span className="font-display tabular-nums italic font-extrabold text-slate-100 dark:text-slate-100">
              {currentPage}
            </span>{' '}
            of{' '}
            <span className="font-display tabular-nums italic font-extrabold text-slate-100 dark:text-slate-100">
              {totalPages}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="grid h-8 w-8 place-items-center rounded-full border border-dark-600 bg-dark-900 text-slate-400 transition-colors hover:text-slate-100 hover:border-dark-500 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeftIcon className="h-3.5 w-3.5" />
            </button>
            {getPageNumbers(currentPage, totalPages).map((page, index) =>
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  aria-hidden
                  className="px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600"
                >
                  …
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page as number)}
                  className={`grid h-8 min-w-[2rem] place-items-center rounded-full px-2.5 text-[11px] font-bold tabular-nums transition-colors ${
                    currentPage === page
                      ? 'bg-[#212938] text-white shadow-[0_4px_12px_rgba(33,41,56,0.18)]'
                      : 'border border-dark-600 bg-dark-900 text-slate-400 hover:text-slate-100 hover:border-dark-500'
                  }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="grid h-8 w-8 place-items-center rounded-full border border-dark-600 bg-dark-900 text-slate-400 transition-colors hover:text-slate-100 hover:border-dark-500 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRightIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </footer>
      )}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Editorial chip — semantic severity in tone-accent palette                  */
/* -------------------------------------------------------------------------- */

type ChipKind = 'tone' | 'low' | 'med' | 'high' | 'crit' | 'muted'

const CHIP_STYLES: Record<ChipKind, { bg: string; fg: string; border: string }> = {
  tone: {
    bg: 'rgb(var(--color-secondary-sand-300) / 0.18)',
    fg: 'rgb(var(--color-secondary-sand-500))',
    border: 'rgb(var(--color-secondary-sand-500) / 0.35)',
  },
  low: {
    bg: 'rgba(74, 155, 110, 0.15)',
    fg: '#4A9B6E',
    border: 'rgba(74, 155, 110, 0.35)',
  },
  med: {
    bg: 'rgb(var(--color-secondary-blue-300) / 0.18)',
    fg: 'rgb(var(--color-secondary-blue-500))',
    border: 'rgb(var(--color-secondary-blue-500) / 0.35)',
  },
  high: {
    bg: 'rgb(var(--color-secondary-sand-500) / 0.15)',
    fg: 'rgb(var(--color-secondary-sand-500))',
    border: 'rgb(var(--color-secondary-sand-500) / 0.35)',
  },
  crit: {
    bg: 'rgba(229, 115, 115, 0.15)',
    fg: '#E57373',
    border: 'rgba(229, 115, 115, 0.35)',
  },
  muted: {
    bg: 'rgba(148, 163, 184, 0.10)',
    fg: 'rgb(148, 163, 184)',
    border: 'rgba(148, 163, 184, 0.30)',
  },
}

function EditorialChip({
  label,
  kind,
  title,
}: {
  label: string
  kind: ChipKind
  title?: string
}) {
  const style = CHIP_STYLES[kind]
  return (
    <span
      title={title}
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        background: style.bg,
        color: style.fg,
        border: `1px solid ${style.border}`,
      }}
    >
      {label}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Notice block — editorial info block matching the section voice            */
/* -------------------------------------------------------------------------- */

function NoticeBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 flex items-start gap-2.5 rounded-2xl border border-dark-600 bg-dark-900 px-4 py-3">
      <InformationCircleIcon
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        style={{ color: 'var(--tone-accent-deep)' }}
      />
      <p className="text-[12px] leading-[1.55] text-slate-400">{children}</p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers — kept identical to legacy (only callers shape changed)            */
/* -------------------------------------------------------------------------- */

function initialsOf(name: string | undefined): string {
  if (!name) return '·'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Accounts created within the last 90 days that have never signed in
// are flagged as "New Account" to distinguish them from truly inactive users
function isNewAccount(user: UserDetailItem): boolean {
  if (!user.accountCreatedDate || user.lastSignInDateTime) return false
  const created = new Date(user.accountCreatedDate)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
  return diffDays <= 90
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`

  return date.toLocaleDateString()
}

function getIssueLabel(issueType: string): string {
  if (!issueType) return 'No Issues'
  switch (issueType) {
    case 'DisabledWithLicenses': return 'Disabled w/ Licenses'
    case 'InactiveWithLicenses': return 'Inactive w/ Licenses'
    case 'InactiveMembers': return 'Inactive Member'
    case 'InactiveGuests': return 'Inactive Guest'
    case 'NoMfa': return 'No MFA'
    case 'NoAuthenticatorApp': return 'No Authenticator'
    case 'NoPhishResistantMfa': return 'No Phish-Resistant'
    case 'HasPhishResistantMfa': return 'Phish-Resistant'
    case 'LicenseWaste': return 'License Waste'
    default: return issueType
  }
}

function getIssueKind(issueType: string): ChipKind {
  if (!issueType) return 'low'
  switch (issueType) {
    case 'DisabledWithLicenses':
    case 'NoMfa':
      return 'crit'
    case 'InactiveWithLicenses':
    case 'InactiveMembers':
    case 'NoAuthenticatorApp':
    case 'NoPhishResistantMfa':
    case 'LicenseWaste':
      return 'high'
    case 'InactiveGuests':
      return 'med'
    case 'HasPhishResistantMfa':
      return 'low'
    default:
      return 'muted'
  }
}

function getPageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, 5, '...', totalPages]
  }

  if (currentPage >= totalPages - 2) {
    return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  }

  return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages]
}
